import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Linkedin, Phone, Mail, ShieldCheck } from "lucide-react";
import { LogoMark } from "./Logo";
import { BUSINESS, CERTS } from "@/content/site";

const ICONS = { Facebook, Instagram, YouTube: Youtube, LinkedIn: Linkedin };
const LINKS = [
  ["Home", "/"], ["Services", "/services"], ["Solar Installation", "/solar-installation"], ["Solar Batteries", "/solar-batteries"],
  ["About", "/about"], ["Service Areas", "/service-areas"], ["Contact", "/contact"], ["Privacy Policy", "/privacy-policy"], ["Terms of Service", "/terms-of-service"],
];

export const Footer = () => (
  <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10 bg-[#060708] pb-28 md:pb-10">
    <div className="pointer-events-none absolute -bottom-40 left-1/2 h-80 w-[70%] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]" />
    <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-20">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <LogoMark className="h-12 w-16" />
            <p className="font-display text-lg font-semibold tracking-[0.12em] text-white">SOLAR ENERGY DOC LLC</p>
          </div>
          <p className="mt-5 font-display text-2xl text-zinc-200">Quality You Can Trust.</p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400">Licensed and insured solar, battery and electrical services for homeowners and businesses across Texas.</p>
          <div className="mt-7 space-y-3 text-sm">
            <a href={BUSINESS.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 text-white hover:text-amber-400 transition-colors"><Phone className="h-4 w-4 text-amber-400" />{BUSINESS.phoneDisplay}</a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email" className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors"><Mail className="h-4 w-4 text-amber-400" />{BUSINESS.email}</a>
          </div>
        </div>
        <div className="lg:col-span-3">
          <p className="eyebrow">Navigate</p>
          <ul className="mt-5 grid grid-cols-2 lg:grid-cols-1 gap-x-6 gap-y-3">
            {LINKS.map(([l, to]) => (
              <li key={to}><Link to={to} data-testid={`footer-link-${to.replace(/\//g, "") || "home"}`} className="text-sm text-zinc-300 hover:text-white transition-colors">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <p className="eyebrow">Credentials</p>
          <ul className="mt-5 grid grid-cols-2 gap-3">
            {CERTS.map((c) => (
              <li key={c.key} className="glass rounded-xl px-4 py-3.5 flex items-center gap-2.5">
                <ShieldCheck className="h-4 w-4 shrink-0 text-amber-400" />
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-200">{c.title}</span>
              </li>
            ))}
          </ul>
          <p className="eyebrow mt-9">Follow</p>
          <div className="mt-4 flex gap-3">
            {BUSINESS.socials.filter((s) => s.url).map((s) => {
              const Icon = ICONS[s.name];
              return (
                <a key={s.name} href={s.url} aria-label={`Solar Energy Doc on ${s.name}`} data-testid={`footer-social-${s.name.toLowerCase()}`}
                  target="_blank" rel="noopener noreferrer"
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/12 text-zinc-300 transition-[color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-amber-400/60 hover:text-amber-400">
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-16 hairline" />
      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-zinc-400">
        <p data-testid="footer-copyright">© 2026 Solar Energy Doc LLC. All Rights Reserved.</p>
        <p className="font-mono uppercase tracking-[0.2em]">Serving All of Texas</p>
      </div>
    </div>
  </footer>
);
